import axios from "axios";
import { supabase } from "./supabaseClient"; // Importar o cliente Supabase

// Obter API URL do sistema de configuração dinâmica
const getApiUrl = () => {
  return (
    window.ENV?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL ||
    "/api"
  );
};

const API_URL = getApiUrl();

const axiosInstance = axios.create({
  baseURL: API_URL,
});

export const orderService = {
  async createOrder(orderData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para criar encomendas."
        );
      }

      const response = await axiosInstance.post(`/orders`, orderData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao criar encomenda"
        );
      }
      throw error;
    }
  },

  async getOrders(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar encomendas."
        );
      }

      const response = await axiosInstance.get(`/orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar encomendas"
        );
      }
      throw error;
    }
  },

  async getOrderById(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar detalhes da encomenda."
        );
      }

      const response = await axiosInstance.get(`/orders/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar detalhes da encomenda"
        );
      }
      throw error;
    }
  },

  async updateOrder(id, orderData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para atualizar encomendas."
        );
      }

      const response = await axiosInstance.put(`/orders/${id}`, orderData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao atualizar encomenda"
        );
      }
      throw error;
    }
  },

  async splitOrder(id, splitData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para dividir encomendas."
        );
      }

      const response = await axiosInstance.post(
        `/orders/${id}/split`,
        splitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao dividir encomenda"
        );
      }
      throw error;
    }
  },

  async getOrderedQuantityByProduct(productId, startDate, endDate) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar quantidade encomendada por produto."
        );
      }

      const response = await axiosInstance.get(
        `/orders/ordered-quantity-by-product`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { productId, startDate, endDate },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message ||
            "Erro ao buscar quantidade encomendada por produto"
        );
      }
      throw error;
    }
  },

  async exportOrders(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para exportar encomendas."
        );
      }

      const response = await axiosInstance.get(`/orders/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
        responseType: 'blob',
      });

      // Create a blob URL and download the file
      const isPdf = (filters.format === 'pdf');
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || (isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = isPdf ? 'encomendas.pdf' : 'encomendas.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao exportar encomendas"
        );
      }
      throw error;
    }
  },

  async cancelWeekly(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.patch(`/orders/${id}/cancel-weekly`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || "Erro ao cancelar recorrência semanal");
      }
      throw error;
    }
  },

  async getWeeklySchedulerLogs(limit = 20) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.get(`/orders/weekly/scheduler-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || "Erro ao buscar logs do scheduler semanal");
      }
      throw error;
    }
  },

  async retryWeekly(rootOrderId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.post(`/orders/${rootOrderId}/retry-weekly`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || "Erro ao recriar encomenda semanal");
      }
      throw error;
    }
  },

  async reactivateWeekly(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.patch(`/orders/${id}/reactivate-weekly`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || "Erro ao reativar recorrência semanal");
      }
      throw error;
    }
  },

  async getWeeklyChains() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.get(`/orders/weekly/chains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || "Erro ao buscar cadeias de encomendas semanais");
      }
      throw error;
    }
  },

  // ⚠️ APENAS EM DESENVOLVIMENTO: Deletar encomenda
  async deleteOrder(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para deletar encomendas."
        );
      }

      const response = await axiosInstance.delete(`/orders/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao deletar encomenda"
        );
      }
      throw error;
    }
  },
};
